import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { pmHistoryPluginKey } from '@atlaskit/editor-common/utils';
import type { ReadonlyTransaction } from '@atlaskit/editor-prosemirror/state';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';
import type { AcceptedSuggestionGroup, AiSuggestionsPluginState } from '../main';

/**
 * The set of suggestion ids across all groups in an accepted-suggestion pool. Used to diff the
 * undoable pool before/after an undo/redo so the backend status can be reverted to `PENDING`
 * (restored by undo) or re-applied as `ACTIONED` (re-accepted by redo).
 */
export function collectSuggestionIds(groups: AcceptedSuggestionGroup[] | undefined): Set<string> {
	const ids = new Set<string>();
	for (const group of groups ?? []) {
		for (const suggestion of group.suggestions) {
			ids.add(suggestion.id);
		}
	}
	return ids;
}

/**
 * The suggestions currently held in plugin state, in navigation order. Used to rebuild
 * the suggestion maps against a new document when the pool changes (undo/redo/accept).
 */
export function getCurrentSuggestions(pluginState: AiSuggestionsPluginState): Suggestion[] {
	return pluginState.orderedSuggestionIds
		.map((id) => pluginState.suggestionById.get(id))
		.filter((suggestion): suggestion is Suggestion => suggestion !== undefined);
}

/**
 * The meta the prosemirror-history plugin attaches to its own undo/redo transactions,
 * under `pmHistoryPluginKey` ("history$"). `{ redo: false }` for an undo, `{ redo: true }`
 * for a redo. This is the same detection pattern used by other editor plugins (card,
 * code-block, etc.).
 */
type PmHistoryMeta = { redo: boolean };

function isPmHistoryMeta(meta: unknown): meta is PmHistoryMeta {
	return (
		typeof meta === 'object' && meta !== null && typeof (meta as PmHistoryMeta).redo === 'boolean'
	);
}

/**
 * Detect whether `tr` is a native history undo or redo by reading the history plugin's meta.
 *
 * Note: `undoDepth`/`redoDepth` from `@atlaskit/prosemirror-history` cannot be used here —
 * they look the history state up by the library's own internal `historyKey`, but the editor
 * registers the history plugin under a different key (`pmHistoryPluginKey`), so those helpers
 * always return 0. Reading the transaction meta is the reliable, collab-safe approach.
 *
 * Returns `undefined` when the transaction is not an undo/redo.
 */
export function getUndoRedoDirection(tr: ReadonlyTransaction): 'undo' | 'redo' | undefined {
	const historyMeta = tr.getMeta(pmHistoryPluginKey);
	if (!isPmHistoryMeta(historyMeta)) {
		return undefined;
	}
	return historyMeta.redo ? 'redo' : 'undo';
}

type HistoryBranchState = { done?: { eventCount?: number } };

/**
 * The undo depth (number of undoable events on the `done` branch) carried by a native
 * undo/redo transaction's `history$` meta — i.e. the depth *after* the transaction is applied.
 * Returns `undefined` when the transaction is not an undo/redo or the meta lacks the depth.
 */
export function getUndoDepthFromHistoryTr(tr: ReadonlyTransaction): number | undefined {
	const historyMeta = tr.getMeta(pmHistoryPluginKey) as
		| { historyState?: HistoryBranchState }
		| undefined;
	return historyMeta?.historyState?.done?.eventCount;
}

/**
 * The current undo depth from the shared history plugin state. Read at accept time to record
 * where the accept sits on the history stack.
 */
export function getCurrentUndoDepth(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
): number {
	return api?.history?.sharedState.currentState()?.done.eventCount ?? 0;
}
